import {
  Button,
  Modal,
  type ModalProps,
  Notification,
  Stack,
  Table,
} from '@omnipresentgroup/design-system';
import { ContractorInvoiceStatus } from '@omnipresentgroup/features';
import {
  type ContractorInvoice,
  ContractorInvoiceStateEnum,
} from '@omnipresentgroup/types';
import {
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useMemo } from 'react';

type Props = {
  modalProps: Omit<ModalProps, 'children'>;
  handleClose: () => void;
  invoices: ContractorInvoice[];
};

const MODAL_TITLE = 'Unable to pay selected invoices';
const NOTIFICATION_TITLE =
  'You may only select invoices that are open, and have a single invoice currency (e.g. EUR) and single billing currency (e.g. USD)';

export const PaymentsSummaryModal = (props: Props) => {
  const { modalProps, invoices, handleClose } = props;

  /** Table Columns */
  const columnHelper = createColumnHelper<ContractorInvoice>();

  const columns = useMemo(
    () => [
      columnHelper.display({
        id: 'invoice',
        cell: (info) => info.row.original.invoiceRef,
        header: () => 'Invoice',
      }),
      columnHelper.display({
        id: 'currency',
        cell: (info) => info.row.original.currency,
        header: () => 'Invoice currency',
      }),
      columnHelper.display({
        id: 'billing-currency',
        cell: (info) => info.row.original.billingCurrency,
        header: () => 'Billing currency',
      }),
      columnHelper.display({
        id: 'manager-state',
        cell: (info) => (
          <ContractorInvoiceStatus
            invoice={info?.row.original}
            statusOverride={{
              [ContractorInvoiceStateEnum.OPEN]: {
                intent: 'warning',
                text: 'Open',
              },
              [ContractorInvoiceStateEnum.IN_PROGRESS]: {
                text: 'Awaiting transfer',
              },
            }}
          />
        ),
        header: () => 'Status',
      }),
    ],
    [columnHelper],
  );

  const table = useReactTable({
    data: invoices,
    columns: columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <Modal {...modalProps} span={5}>
      <Modal.Header title={MODAL_TITLE} />

      <Modal.Body>
        <Stack gap="24">
          <Notification
            id="bulk-payment-summary-notification"
            title={NOTIFICATION_TITLE}
            intent="warning"
          />
          <Table id={'invoice-summary-list'} table={table} showHeader={false} />
        </Stack>
      </Modal.Body>

      <Modal.Footer>
        <Stack gap="4">
          <Button onClick={() => handleClose()}>Close</Button>
        </Stack>
      </Modal.Footer>
    </Modal>
  );
};
